/**
 * eco-ptz-content.ts
 * ─────────────────────────────────────────────────────────────────
 * Source unique du contenu éditorial de la page-guide
 * /particuliers/aides/eco-ptz.
 *
 * RÈGLES ÉDITORIALES (AGENTS §8) :
 *  - Plafond max actuel 50 000 € (rénovation globale, gain ≥ 35 %).
 *  - Durée max 20 ans (depuis 2022).
 *  - Sans condition de ressources.
 *  - PAS de mention "toutes les banques partenaires" (seules
 *    les banques signataires d'une convention avec l'État).
 */

// ════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════

export interface EcoPtzSummaryPoint {
  text: string;
}

export interface EcoPtzPlafond {
  /** Description de l'action / bouquet */
  action: string;
  /** Plafond du prêt en € */
  plafond: string;
  /** Durée de remboursement max */
  duree: string;
  /** Note éventuelle */
  note?: string;
}

export interface EcoPtzTravaux {
  iconName: string;
  title: string;
  items: string[];
}

export interface EcoPtzCondition {
  iconName: string;
  title: string;
  description: string;
}

export interface EcoPtzDemarche {
  step: number;
  iconName: string;
  title: string;
  description: string;
}

export interface EcoPtzCumul {
  aide: string;
  iconName: string;
  description: string;
  tone: 'brand' | 'accent' | 'sage' | 'lavender' | 'ivory';
  href?: string;
}

export interface EcoPtzFaqItem {
  question: string;
  answer: string;
}

// ════════════════════════════════════════════════════════════════
// HERO
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_HERO = {
  eyebrow: 'Prêt à taux zéro',
  titlePrimary: 'Éco-PTZ',
  titleAccent: 'le prêt 0 % pour vos travaux',
  lead:
    "Le prêt à taux zéro finance vos travaux d'amélioration énergétique sans intérêts. Vous remboursez uniquement le capital emprunté, sans frais de dossier. Accessible à tous les propriétaires sans condition de ressources, c'est l'outil idéal pour financer le reste à charge après les aides.",
  amountHeadline: "Jusqu'à 50 000 €",
  amountSubline:
    'Sur 20 ans maximum, sans intérêts, sans frais de dossier. Plafond atteint sur les rénovations globales avec un gain énergétique d\'au moins 35 %.',
  microPromise: 'Sans condition de ressources. Sans frais. Cumulable avec toutes les aides.',
} as const;

export const ECO_PTZ_SUMMARY: EcoPtzSummaryPoint[] = [
  {
    text:
      "Le prêt à taux zéro est un prêt sans intérêts : vous remboursez uniquement le capital, sans aucun frais (les intérêts sont pris en charge par l'État).",
  },
  {
    text:
      'Aucune condition de ressources : tous les propriétaires occupants ou bailleurs peuvent en faire la demande.',
  },
  {
    text:
      "Le plafond varie selon le type de travaux : 7 000 € pour des fenêtres, 15 000 € pour un autre geste, jusqu'à 50 000 € pour une rénovation globale (gain ≥ 35 %).",
  },
  {
    text:
      "Cumulable avec l'aide de l'État, la prime énergie, la TVA 5,5 % et les aides locales. Ce prêt finance souvent la part qui reste à votre charge après aides.",
  },
];

// ════════════════════════════════════════════════════════════════
// MÉCANISME — Comment ça marche
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_MECANISME = {
  eyebrow: 'Comment ça marche',
  title: "Un prêt classique, mais sans intérêts ni frais",
  description:
    "Le prêt est distribué par les banques conventionnées par l'État. Vous empruntez le capital nécessaire au financement des travaux ; l'État rembourse les intérêts à la banque sous forme de crédit d'impôt. Côté ménage, c'est transparent : vous ne payez que le capital.",
} as const;

// ════════════════════════════════════════════════════════════════
// PLAFONDS PAR TYPE DE TRAVAUX
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_PLAFONDS: EcoPtzPlafond[] = [
  {
    action: 'Action unique sur fenêtres / portes-fenêtres',
    plafond: '7 000 €',
    duree: '15 ans',
    note: 'Remplacement de simple vitrage par du double / triple vitrage',
  },
  {
    action: 'Une seule action (hors fenêtres seules)',
    plafond: '15 000 €',
    duree: '15 ans',
    note: 'Isolation, chauffage performant, eau chaude solaire / thermodynamique, etc.',
  },
  {
    action: '2 actions (bouquet de travaux)',
    plafond: '25 000 €',
    duree: '15 ans',
  },
  {
    action: '3 actions ou plus (bouquet de travaux)',
    plafond: '30 000 €',
    duree: '15 ans',
  },
  {
    action: 'Rénovation globale (gain énergétique ≥ 35 %)',
    plafond: '50 000 €',
    duree: '20 ans',
    note: 'Diagnostic énergétique préalable + suivi obligatoire',
  },
  {
    action: "Réhabilitation d'un dispositif d'assainissement non collectif",
    plafond: '10 000 €',
    duree: '15 ans',
  },
];

// ════════════════════════════════════════════════════════════════
// TRAVAUX ÉLIGIBLES
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_TRAVAUX: EcoPtzTravaux[] = [
  {
    iconName: 'flame',
    title: 'Chauffage & eau chaude',
    items: [
      'Pompe à chaleur (air-eau, géothermique, hybride)',
      'Chauffe-eau solaire / thermodynamique',
      'Chaudière biomasse',
      'Raccordement à un réseau de chaleur',
    ],
  },
  {
    iconName: 'snowflake',
    title: 'Isolation thermique',
    items: [
      'Isolation des combles',
      'Isolation des murs (intérieur / extérieur)',
      'Isolation des planchers bas',
      'Remplacement de fenêtres',
    ],
  },
  {
    iconName: 'wind',
    title: 'Ventilation & ENR',
    items: [
      'VMC double flux',
      'Système solaire combiné',
      'Bouquet de travaux mixant plusieurs catégories',
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// CONDITIONS
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_CONDITIONS: EcoPtzCondition[] = [
  {
    iconName: 'home',
    title: 'Résidence principale',
    description:
      "Le logement doit être utilisé comme résidence principale. Les bailleurs s'engagent à louer le bien en résidence principale.",
  },
  {
    iconName: 'gauge',
    title: 'Logement de plus de 2 ans',
    description:
      "Logement achevé depuis plus de 2 ans à la date de début des travaux. Pas de critère d'âge minimum strict (à la différence de l'aide de l'État).",
  },
  {
    iconName: 'hard-hat',
    title: 'Professionnel certifié',
    description:
      "Travaux réalisés par un professionnel certifié pour la catégorie de travaux concernée.",
  },
  {
    iconName: 'landmark',
    title: 'Banque conventionnée',
    description:
      "Prêt accordé par une banque ayant signé une convention avec l'État. La majorité des grandes banques françaises sont conventionnées.",
  },
];

// ════════════════════════════════════════════════════════════════
// DÉMARCHES — 5 ÉTAPES
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_DEMARCHES: EcoPtzDemarche[] = [
  {
    step: 1,
    iconName: 'calculator',
    title: 'Identifier vos travaux éligibles',
    description:
      "Vérifier que les travaux envisagés font partie de la liste des opérations éligibles au prêt (chauffage performant, isolation, ventilation double flux). Notre calculateur croise le prêt avec les aides cumulables.",
  },
  {
    step: 2,
    iconName: 'hard-hat',
    title: 'Faire établir un devis par un professionnel certifié',
    description:
      "L'artisan doit être certifié pour la catégorie de travaux. Il vous remet un devis détaillé, accompagné du formulaire de prêt à taux zéro (fourni par votre banque).",
  },
  {
    step: 3,
    iconName: 'file-signature',
    title: 'Déposer la demande auprès de votre banque',
    description:
      "Vous présentez le dossier à la banque conventionnée de votre choix : devis, formulaire signé par l'artisan, avis d'imposition, justificatif de propriété.",
  },
  {
    step: 4,
    iconName: 'wallet',
    title: "Signer l'offre de prêt et lancer les travaux",
    description:
      "Une fois l'offre acceptée, vous disposez d'un délai pour démarrer les travaux (généralement 3 ans). Les fonds sont débloqués au fur et à mesure (paiement des factures par la banque).",
  },
  {
    step: 5,
    iconName: 'shield-check',
    title: 'Justifier la réalisation des travaux',
    description:
      "À la fin des travaux, vous transmettez à la banque les factures et attestations de l'artisan. La banque vérifie la conformité.",
  },
];

// ════════════════════════════════════════════════════════════════
// CUMUL AVEC AUTRES AIDES
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_CUMULS: EcoPtzCumul[] = [
  {
    aide: "MaPrimeRénov'",
    iconName: 'piggy-bank',
    description:
      "Le prêt à taux zéro finance souvent le reste à charge après l'aide de l'État. Résultat : vous pouvez rénover sans rien avancer.",
    tone: 'brand',
    href: '/particuliers/aides/maprimerenov',
  },
  {
    aide: 'Prime énergie',
    iconName: 'sparkles',
    description:
      "Cumulable à 100 %. La prime énergie réduit le coût, le prêt à taux zéro finance ce qui reste — sans intérêts.",
    tone: 'accent',
    href: '/particuliers/aides/cee',
  },
  {
    aide: 'TVA à 5,5 %',
    iconName: 'percent',
    description:
      "Le taux réduit s'applique sur les factures finançables par le prêt. Économie automatique sans démarche supplémentaire.",
    tone: 'sage',
    href: '/particuliers/aides/tva-reduite',
  },
  {
    aide: 'Aides locales',
    iconName: 'landmark',
    description:
      'Aides des collectivités (Régions, Départements, métropoles, intercommunalités) : variables selon votre territoire.',
    tone: 'ivory',
  },
];

// ════════════════════════════════════════════════════════════════
// FAQ
// ════════════════════════════════════════════════════════════════

export const ECO_PTZ_FAQ: EcoPtzFaqItem[] = [
  {
    question: "Qui peut bénéficier du prêt à taux zéro ?",
    answer:
      "Les propriétaires occupants et bailleurs, les copropriétaires (prêt individuel en copropriété), et les syndicats de copropriétaires (prêt collectif). Aucune condition de revenus. Le logement doit être une résidence principale et avoir plus de 2 ans.",
  },
  {
    question: 'Quelles banques le proposent ?',
    answer:
      "Toutes les banques signataires d'une convention avec l'État : BNP Paribas, Société Générale, Crédit Agricole, LCL, Crédit Mutuel, Banque Populaire, Caisse d'Épargne, La Banque Postale… Vous pouvez choisir librement votre banque.",
  },
  {
    question: 'Quel est le plafond en 2026 ?',
    answer:
      "7 000 € pour des fenêtres seules, 15 000 € pour un autre geste isolé, 25 000 € pour 2 actions, 30 000 € pour 3 actions ou plus, et jusqu'à 50 000 € pour une rénovation globale avec un gain énergétique d'au moins 35 % (diagnostic préalable requis).",
  },
  {
    question: 'Quelle est la durée de remboursement maximale ?',
    answer:
      "15 ans pour la plupart des prêts, étendue à 20 ans pour les rénovations globales (gain ≥ 35 %). Aucun intérêt n'est facturé — c'est l'État qui prend en charge le différentiel.",
  },
  {
    question: "Ce prêt est-il cumulable avec l'aide de l'État ?",
    answer:
      "Oui, intégralement. Le cumul prêt à taux zéro + aide de l'État + prime énergie + TVA à 5,5 % est l'architecture standard pour financer une rénovation : les aides couvrent une partie du coût, le prêt finance le reste sans intérêts.",
  },
  {
    question: 'Puis-je demander plusieurs prêts sur le même logement ?',
    answer:
      "Oui : depuis 2022, vous pouvez bénéficier d'un prêt complémentaire si vous n'avez pas atteint le plafond maximal (50 000 € en rénovation globale). Le second prêt doit être demandé dans les 5 ans suivant le premier.",
  },
  {
    question: 'Que se passe-t-il si je revends mon logement ?',
    answer:
      "En cas de revente, le solde du prêt devient remboursable par anticipation (sauf cas particuliers). Renseignez-vous auprès de votre banque avant tout projet de vente.",
  },
  {
    question: 'Solution Éco France peut-elle m\'aider ?',
    answer:
      "Notre rôle : croiser le prêt à taux zéro avec les autres aides pour optimiser votre plan de financement. Nous ne prêtons pas l'argent (c'est le rôle de votre banque), mais nous préparons le dossier technique (devis, formulaire pré-rempli) pour faciliter votre démarche bancaire.",
  },
];
